import {
  AlertTriangle,
  Bookmark,
  Diff,
  Newspaper,
  Server,
  ShieldAlert,
  X,
} from "lucide-react";
import type { QueuedNotification } from "../../contexts/DNDContext";
import type { ServerNotification } from "../../contexts/NotificationsContext";
import {
  categoryToQueuedType,
  isErrorQueuedNotification,
  isErrorServerNotification,
} from "../../lib/notification-presentation";
import { timeAgo } from "../../lib/time-utils";
import type { DeskInboxItem } from "../../lib/services/desk-inbox";

export function notificationIcon(
  type: QueuedNotification["type"],
  error = false,
) {
  if (error)
    return (
      <AlertTriangle
        aria-hidden="true"
        className="h-3.5 w-3.5 text-red-400"
      />
    );
  switch (type) {
    case "iv":
      return (
        <Diff
          aria-hidden="true"
          className="h-3.5 w-3.5 text-[var(--fintheon-accent)]"
        />
      );
    case "news":
      return (
        <Newspaper
          aria-hidden="true"
          className="h-3.5 w-3.5 text-[var(--fintheon-accent)]"
        />
      );
    case "tilt":
      return (
        <AlertTriangle
          aria-hidden="true"
          className="h-3.5 w-3.5 text-orange-400"
        />
      );
    case "trade":
      return (
        <ShieldAlert
          aria-hidden="true"
          className="h-3.5 w-3.5 text-blue-400"
        />
      );
    case "warning":
      return (
        <AlertTriangle
          aria-hidden="true"
          className="h-3.5 w-3.5 text-red-400"
        />
      );
    case "system":
      return (
        <Server
          aria-hidden="true"
          className="h-3.5 w-3.5 text-[var(--fintheon-muted)]"
        />
      );
  }
}

export function ServerNotificationRow({
  notification,
  onRead,
  onClear,
}: {
  notification: ServerNotification;
  onRead: (id: string) => Promise<void>;
  onClear: (id: string) => Promise<void>;
}) {
  const isError = isErrorServerNotification(notification);
  const type = categoryToQueuedType(notification.category);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (!notification.read) void onRead(notification.id);
      }}
      onKeyDown={(event) => {
        if ((event.key === "Enter" || event.key === " ") && !notification.read)
          void onRead(notification.id);
      }}
      className="s156-bulletin-inbox-row group flex items-start gap-2.5 px-2.5 py-2"
      data-read={notification.read ? "true" : "false"}
    >
      <div className="mt-0.5 shrink-0">{notificationIcon(type, isError)}</div>
      <div className="min-w-0 flex-1">
        <div
          className={`s156-bulletin-row-title truncate text-[11px] font-semibold ${
            notification.read
              ? "text-[var(--fintheon-muted)]"
              : "text-[var(--fintheon-text)]"
          }`}
        >
          {notification.title}
        </div>
        <div className="mt-0.5 line-clamp-2 text-[10px] leading-relaxed text-[var(--fintheon-muted)]">
          {notification.body}
        </div>
        <div className="mt-1 flex items-center gap-2 text-[8px] uppercase tracking-[0.12em] text-[var(--fintheon-muted)]/55">
          <span>{formatCategory(notification.category)}</span>
          <span>{timeAgo(notification.createdAt)}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          void onClear(notification.id);
        }}
        className="s156-bulletin-icon-control shrink-0 opacity-70 hover:opacity-100"
        title="Dismiss"
        aria-label={`Dismiss ${notification.title}`}
      >
        <X aria-hidden="true" className="h-3 w-3" />
      </button>
    </div>
  );
}

export function DeskActionNotificationRow({
  item,
  isBusy,
  onApprove,
  onDeny,
}: {
  item: DeskInboxItem;
  isBusy: boolean;
  onApprove: () => void;
  onDeny: () => void;
}) {
  const isRegime = item.type === "regime_change";

  const bookmarkEvent = () => {
    try {
      const key = "fintheon:pending-bookmarks:inbox-events";
      const current = JSON.parse(localStorage.getItem(key) ?? "[]");
      localStorage.setItem(
        key,
        JSON.stringify([
          ...current,
          { id: item.id, title: item.title, summary: item.summary },
        ]),
      );
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="s156-bulletin-inbox-row group px-2.5 py-2">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 shrink-0">
          {isRegime ? (
            <Diff
              aria-hidden="true"
              className="h-3.5 w-3.5 text-[var(--fintheon-accent)]"
            />
          ) : (
            <ShieldAlert
              aria-hidden="true"
              className="h-3.5 w-3.5 text-[var(--fintheon-accent)]"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-semibold text-[var(--fintheon-text)]">
            {item.title}
          </div>
          <div className="mt-0.5 line-clamp-2 text-[10px] leading-relaxed text-[var(--fintheon-muted)]">
            {item.summary || item.body}
          </div>
          <div className="mt-1 text-[8px] uppercase tracking-[0.12em] text-[var(--fintheon-muted)]/55">
            {isRegime ? "Regime change" : "Desk request"} · Harper
          </div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
        <BulletinActionButton
          label="Approve"
          disabled={isBusy}
          onClick={onApprove}
        />
        <BulletinActionButton label="Deny" disabled={isBusy} onClick={onDeny} />
        {isRegime ? (
          <button
            type="button"
            disabled={isBusy}
            onClick={bookmarkEvent}
            className="inline-flex min-h-8 items-center gap-1 rounded px-2 text-[9px] uppercase tracking-[0.1em] text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] disabled:opacity-35"
            title="Bookmark notification"
          >
            <Bookmark aria-hidden="true" className="h-3 w-3" />
            Bookmark
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function QueuedNotificationRow({
  notification,
  onDismiss,
}: {
  notification: QueuedNotification;
  onDismiss: (id: string) => void;
}) {
  const isError = isErrorQueuedNotification(notification);
  return (
    <div className="s156-bulletin-inbox-row group flex items-start gap-2.5 px-2.5 py-2">
      <div className="mt-0.5 shrink-0">
        {notificationIcon(notification.type, isError)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[11px] font-semibold text-[var(--fintheon-text)]">
          {notification.title}
        </div>
        <div className="mt-0.5 line-clamp-2 text-[10px] leading-relaxed text-[var(--fintheon-muted)]">
          {notification.message}
        </div>
        <div className="mt-1 text-[8px] uppercase tracking-[0.12em] text-[var(--fintheon-muted)]/55">
          Queued · {timeAgo(notification.timestamp.toISOString())}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(notification.id)}
        className="s156-bulletin-icon-control shrink-0 opacity-70 hover:opacity-100"
        title="Dismiss"
        aria-label={`Dismiss ${notification.title}`}
      >
        <X aria-hidden="true" className="h-3 w-3" />
      </button>
    </div>
  );
}

export function formatCategory(category: string): string {
  return category.replace(/_/g, " ");
}

function BulletinActionButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="min-h-8 rounded px-2 text-[9px] uppercase tracking-[0.1em] text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] disabled:opacity-35"
    >
      {label}
    </button>
  );
}
